import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Collapse,
} from '@mui/material';
import type { BotRun } from '../../types';
import { getBotRuns } from '../../services/bots';

const STATUS_COLORS: Record<string, 'success' | 'error' | 'info'> = {
  completed: 'success',
  failed: 'error',
  running: 'info',
};

interface BotRunHistoryProps {
  open: boolean;
  onClose: () => void;
  botId: string | null;
  botName: string;
}

export default function BotRunHistory({ open, onClose, botId, botName }: BotRunHistoryProps) {
  const [runs, setRuns] = useState<BotRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !botId) return;
    setLoading(true);
    getBotRuns(botId)
      .then(setRuns)
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [open, botId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Run History — {botName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : runs.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No runs yet
          </Typography>
        ) : (
          <List disablePadding>
            {runs.map((run) => (
              <Box key={run.id}>
                <ListItemButton
                  onClick={() => setExpandedId(expandedId === run.id ? null : run.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={run.status}
                          size="small"
                          color={STATUS_COLORS[run.status] ?? 'default'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {run.triggeredBy}
                        </Typography>
                      </Box>
                    }
                    secondary={new Date(run.createdAt).toLocaleString()}
                  />
                </ListItemButton>
                <Collapse in={expandedId === run.id}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85rem',
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      mx: 1,
                      mb: 1,
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    {run.result || 'No output'}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
